import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { LOGO_FULL } from "../lib/vehicleImages";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(location.state?.error || null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await login(email, password);
      navigate(dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_FULL} alt="ATLAS" className="h-12 w-auto mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-1">Sign in to your Atlas account.</p>
        </div>
        <form onSubmit={submit} className="bg-card border border-border rounded-md p-6 md:p-8 space-y-5">
          <GoogleSignInButton label="Continue with Google" />
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or sign in with email <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" required data-testid="login-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password" required data-testid="login-password" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full bg-primary hover:bg-atlas-blue text-white py-6">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : <><LogIn className="h-4 w-4 mr-2" /> Sign in</>}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            New to Atlas?{" "}
            <Link to="/register" className="text-atlas-blue font-medium" data-testid="login-goto-register">Create an account</Link>
          </p>
          <p className="text-sm text-muted-foreground text-center">
            <Link to="/forgot-password" className="text-atlas-blue" data-testid="login-forgot">Forgot your password?</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
